import React, { useRef, useState, Suspense, lazy, useCallback } from "react";
import Placeholder from "../Placeholder";
import useCompileReady from "../useCompileReady";
import { useGLTF, useCursor, Html } from "@react-three/drei";
import { Select } from "@react-three/postprocessing";
import { extend, useThree } from "@react-three/fiber";
import { geometry } from "maath";
extend(geometry);
import { useProjectFocus } from "@/stores/projectFocusStore";
import { computeFocusFrame } from "../focusUtils";
import useFocusOcclusionHidden from "../useFocusOcclusionHidden";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@nextui-org/react";

// Use React.lazy for code splitting instead of next/dynamic
const PdfViewer = lazy(() => import("@/components/pdf/PdfViewer"));

// Simple debounce utility
function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

// Inner component that actually loads and renders the model
// This suspends, and is wrapped by Suspense in the parent component
function ArchModelInner({
  projectName,
  modelPath,
  pdfFileName,
  // Focus-view azimuth for this model, radians around the vertical axis
  // (negative = clockwise seen from above). 0 = straight front view.
  focusYaw = 0,
  // Multiplier on the auto-fit focus zoom. >1 zooms in closer; use to fix
  // models whose bounding box is inflated by stray geometry in the GLB.
  focusZoom = 1,
  ...props
}) {
  const { nodes, materials } = useGLTF(modelPath);
  const ref = useRef();
  const groupRef = useRef();
  const { size } = useThree();
  // The mesh is assembled inline (geometry + material from the GLTF), so
  // compile against the mounted mesh object captured via callback ref.
  const [meshObject, setMeshObject] = useState(null);
  // Stay hidden (placeholder up) until shaders are compiled off-thread, so
  // the first draw never stutters the visible scene.
  const compiled = useCompileReady(meshObject);

  // Hover state
  const [hovered, hover] = useState(null);
  // Debounce hover a bit to stop the ticker from being erratic
  const debouncedHover = useCallback(debounce(hover, 30), []);
  const over = (name) => (e) => (e.stopPropagation(), debouncedHover(name));
  useCursor(hovered);

  // Modal state
  const [isOpen, setIsOpen] = useState(false);

  const focusId = `arch:${projectName}`;
  // Hide this model while it blocks the view of another focused model.
  const occluded = useFocusOcclusionHidden(focusId, groupRef);

  const openModal = () => {
    setTimeout(() => {
      setIsOpen(true);
    }, 0);
  };

  // First click focuses the camera on the model; clicking it again while
  // focused opens the drawings modal (same as the card's action button).
  const handleMeshClick = (e) => {
    e.stopPropagation();
    const { focused, setFocus } = useProjectFocus.getState();
    if (focused?.id === focusId) {
      openModal();
      return;
    }
    const frame = computeFocusFrame(groupRef.current, size);
    setFocus({
      id: focusId,
      name: projectName,
      ...frame,
      zoom: frame.zoom * focusZoom,
      yaw: focusYaw,
      actionLabel: "View drawings",
      onAction: openModal,
    });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      {!compiled && (
        <Placeholder position={props.position || [0, 0.5, 0]} scale={[2, 2, 2]} />
      )}
      <group
        ref={groupRef}
        dispose={null}
        visible={compiled && !occluded}
        {...props}
      >
        <Select enabled={hovered}>
          <mesh
            ref={(mesh) => {
              ref.current = mesh;
              if (mesh) setMeshObject(mesh);
            }}
            castShadow
            receiveShadow
            geometry={nodes.Mesh.geometry}
            material={materials.Material}
            onPointerOver={() => hover(true)}
            onPointerOut={() => hover(false)}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onClick={handleMeshClick}
          >
            <Html>
              {hovered && !isOpen && (
                <div className="content">{projectName}</div>
              )}
            </Html>
          </mesh>
        </Select>
      </group>

      {/* Only render the modal Html when open to avoid event conflicts */}
      {isOpen && (
        <Html portal={{ current: document.body }} zIndexRange={[100, 0]}>
          <Modal
            size="5xl"
            isOpen={isOpen}
            onClose={handleClose}
            backdrop="blur"
            scrollBehavior="inside"
            placement="center"
            isDismissable={true}
            hideCloseButton={false}
          >
            <ModalContent>
              <ModalHeader className="flex flex-col gap-1">
                {projectName}
              </ModalHeader>
              <ModalBody>
                <Suspense fallback={<div>Loading PDF...</div>}>
                  <PdfViewer pdfFileName={pdfFileName} />
                </Suspense>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onClick={handleClose}>
                  Close
                </Button>
                <Button color="primary" onClick={handleClose}>
                  Action
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Html>
      )}
    </>
  );
}

// Wrapper component that provides Suspense boundary
// This allows each model to load independently with its own placeholder
export default function ArchModel(props) {
  return (
    <Suspense
      fallback={
        <Placeholder
          position={props.position || [0, 0.5, 0]}
          scale={[2, 2, 2]}
        />
      }
    >
      <ArchModelInner {...props} />
    </Suspense>
  );
}

function Annotation({ children, ...props }) {
  return (
    <Html
      {...props}
      transform
      occlude="blending"
      geometry={<roundedPlaneGeometry args={[300, 500, 25]} />}
    >
      <div
        className="flex cursor-pointer items-center justify-center gap-[5px] rounded-full bg-[indianred] text-[8px] font-light tracking-wider text-white shadow-[inset_0_0_0_0.09px_white] transition-all duration-500 ease-in-out hover:text-black hover:shadow-[inset_250px_0_0_0_white]"
      >
        {children}
      </div>
    </Html>
  );
}
